import { trackError } from '#api/errors.ts';
import type { ConfigOverride, ConfigOverrideKey } from '#dn-types/host.ts';
import { mapOverridesToForm } from '#screens/hosts/form/advanced/mappers/mapOverridesToForm.ts';
import {
  DNSMatchDomains,
  DNSResolverAddrs,
  DNSSearchDomains,
  LighthouseAdvertiseAddrs,
  LighthouseInterval,
  ListenBatch,
  ListenHost,
  ListenReadBuffer,
  ListenUDPOffloads,
  ListenWriteBuffer,
  LoggingFormat,
  LoggingLevel,
  LoggingTimestampFormat,
  PreferredRanges,
  PunchyDelay,
  PunchyRespondDelay,
  Routines,
  StaticMapNetwork,
  TunDev,
  TunDisabled,
  TunMTU,
  TunTxQueue,
  TunUseOffloads,
} from '../config-forms/index.ts';

type Props = {
  defaultValues?: readonly ConfigOverride[];
  onSave: (overrides: ConfigOverride[]) => void;
  overrideKey: Exclude<ConfigOverrideKey, 'prometheus' | 'graphite'>;
};

export function ConfigOverrideForm({ overrideKey, defaultValues, onSave }: Props) {
  const defaultFormData = defaultValues && mapOverridesToForm(defaultValues);

  switch (overrideKey) {
    case 'lighthouse.advertise_addrs': {
      const advertise_addrs = defaultFormData?.lighthouse.advertise_addrs;
      return (
        <LighthouseAdvertiseAddrs
          onSave={onSave}
          defaultValues={advertise_addrs ? { lighthouse: { advertise_addrs } } : undefined}
        />
      );
    }
    case 'lighthouse.interval': {
      const interval = defaultFormData?.lighthouse.interval;
      return <LighthouseInterval onSave={onSave} defaultValues={interval ? { lighthouse: { interval } } : undefined} />;
    }
    case 'definednet.dns.resolver_addrs': {
      const resolver_addrs = defaultFormData?.definednet.dns.resolver_addrs;
      return (
        <DNSResolverAddrs
          onSave={onSave}
          defaultValues={resolver_addrs ? { definednet: { dns: { resolver_addrs } } } : undefined}
        />
      );
    }
    case 'definednet.dns.match_domains': {
      const match_domains = defaultFormData?.definednet.dns.match_domains;
      return (
        <DNSMatchDomains
          onSave={onSave}
          defaultValues={match_domains ? { definednet: { dns: { match_domains } } } : undefined}
        />
      );
    }
    case 'definednet.dns.search_domains': {
      const search_domains = defaultFormData?.definednet.dns.search_domains;
      return (
        <DNSSearchDomains
          onSave={onSave}
          defaultValues={search_domains ? { definednet: { dns: { search_domains } } } : undefined}
        />
      );
    }
    case 'preferred_ranges': {
      const preferred_ranges = defaultFormData?.preferred_ranges;
      return <PreferredRanges onSave={onSave} defaultValues={preferred_ranges ? { preferred_ranges } : undefined} />;
    }
    case 'listen.host': {
      const listenHost = defaultFormData?.listen.host;
      return <ListenHost onSave={onSave} defaultValues={listenHost ? { listen: { host: listenHost } } : undefined} />;
    }
    case 'tun.disabled': {
      const tunDisabled = defaultFormData?.tun.disabled;
      return (
        <TunDisabled onSave={onSave} defaultValues={tunDisabled ? { tun: { disabled: tunDisabled } } : undefined} />
      );
    }
    case 'tun.dev': {
      const tunDev = defaultFormData?.tun.dev;
      return <TunDev onSave={onSave} defaultValues={tunDev ? { tun: { dev: tunDev } } : undefined} />;
    }
    case 'tun.mtu': {
      const tunMTU = defaultFormData?.tun.mtu;
      return <TunMTU onSave={onSave} defaultValues={tunMTU ? { tun: { mtu: tunMTU } } : undefined} />;
    }
    case 'tun.tx_queue': {
      const tunTxQueue = defaultFormData?.tun.tx_queue;
      return <TunTxQueue onSave={onSave} defaultValues={tunTxQueue ? { tun: { tx_queue: tunTxQueue } } : undefined} />;
    }
    case 'listen.batch': {
      const listenBatch = defaultFormData?.listen.batch;
      return (
        <ListenBatch onSave={onSave} defaultValues={listenBatch ? { listen: { batch: listenBatch } } : undefined} />
      );
    }
    case 'listen.read_buffer': {
      const listenReadBuffer = defaultFormData?.listen.read_buffer;
      return (
        <ListenReadBuffer
          onSave={onSave}
          defaultValues={listenReadBuffer ? { listen: { read_buffer: listenReadBuffer } } : undefined}
        />
      );
    }
    case 'listen.write_buffer': {
      const listenWriteBuffer = defaultFormData?.listen.write_buffer;
      return (
        <ListenWriteBuffer
          onSave={onSave}
          defaultValues={listenWriteBuffer ? { listen: { write_buffer: listenWriteBuffer } } : undefined}
        />
      );
    }
    case 'listen.udp_offloads': {
      const listenUDPOffloads = defaultFormData?.listen.udp_offloads;
      return (
        <ListenUDPOffloads
          onSave={onSave}
          defaultValues={listenUDPOffloads ? { listen: { udp_offloads: listenUDPOffloads } } : undefined}
        />
      );
    }
    case 'tun.use_offloads': {
      const tunUseOffloads = defaultFormData?.tun.use_offloads;
      return (
        <TunUseOffloads
          onSave={onSave}
          defaultValues={tunUseOffloads ? { tun: { use_offloads: tunUseOffloads } } : undefined}
        />
      );
    }
    case 'routines': {
      const routines = defaultFormData?.routines;
      return <Routines onSave={onSave} defaultValues={routines ? { routines } : undefined} />;
    }
    case 'logging.level': {
      const loggingLevel = defaultFormData?.logging.level;
      return (
        <LoggingLevel onSave={onSave} defaultValues={loggingLevel ? { logging: { level: loggingLevel } } : undefined} />
      );
    }
    case 'logging.format': {
      const loggingFormat = defaultFormData?.logging.format;
      return (
        <LoggingFormat
          onSave={onSave}
          defaultValues={loggingFormat ? { logging: { format: loggingFormat } } : undefined}
        />
      );
    }
    case 'logging.timestamp_format': {
      const loggingTimestampFormat = defaultFormData?.logging.timestamp_format;
      return (
        <LoggingTimestampFormat
          onSave={onSave}
          defaultValues={loggingTimestampFormat ? { logging: { timestamp_format: loggingTimestampFormat } } : undefined}
        />
      );
    }
    case 'static_map.network': {
      const staticMapNetwork = defaultFormData?.static_map.network;
      return (
        <StaticMapNetwork
          onSave={onSave}
          defaultValues={staticMapNetwork ? { static_map: { network: staticMapNetwork } } : undefined}
        />
      );
    }
    case 'punchy.delay': {
      const punchyDelay = defaultFormData?.punchy.delay;
      return (
        <PunchyDelay onSave={onSave} defaultValues={punchyDelay ? { punchy: { delay: punchyDelay } } : undefined} />
      );
    }
    case 'punchy.respond_delay': {
      const punchyRespondDelay = defaultFormData?.punchy.respond_delay;
      return (
        <PunchyRespondDelay
          onSave={onSave}
          defaultValues={punchyRespondDelay ? { punchy: { respond_delay: punchyRespondDelay } } : undefined}
        />
      );
    }
    // Stats are all handled in graphite and prometheus forms
    case 'stats.host':
    case 'stats.prefix':
    case 'stats.namespace':
    case 'stats.subsystem':
    case 'stats.listen':
    case 'stats.path':
    case 'stats.interval':
    case 'stats.type':
    case 'stats.lighthouse_metrics':
    case 'stats.message_metrics':
    case 'lighthouse.remote_allow_list': // handled in different form, but typescript doesn't know that
    case 'lighthouse.local_allow_list': // handled in different form, but typescript doesn't know that
      trackError('Unexpected "Cannot configure here" in tag config override form');
      return 'Cannot configure here';
    // serve_dns is not configurable via tags
    case 'lighthouse.serve_dns':
    case 'lighthouse.dns.port':
    case 'lighthouse.dns.host':
      trackError('Unexpected "Not configurable by tags" in tag config override form');
      return 'Not configurable by tags';
  }
}
